import { FC, useEffect, useState } from 'react';

import { Loader2, Plus, Search } from 'lucide-react';

import { formatOrderAmount } from './orderRevisionUtils';
import { Button } from '../../../components/ui/Button';
import { Image } from '../../../components/ui/Image';
import { Input } from '../../../components/ui/Input';
import { profileOrderEditOffersRoute } from '../../../models/routes';
import { Offer } from '../../../types';

export type OrderEditableOffer = Pick<
    Offer,
    'id' | 'amount' | 'price' | 'property_set' | 'product'
>;

interface Props {
    orderId: number;
    excludedOfferIds: number[];
    onAdd: (offer: OrderEditableOffer) => void;
}

export const OrderOfferSearch: FC<Props> = ({
    orderId,
    excludedOfferIds,
    onAdd,
}) => {
    const [query, setQuery] = useState('');
    const [offers, setOffers] = useState<OrderEditableOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        const timeout = window.setTimeout(
            async () => {
                setLoading(true);
                setError('');

                try {
                    const url = new URL(
                        profileOrderEditOffersRoute(orderId),
                        window.location.origin,
                    );
                    if (query.trim()) url.searchParams.set('q', query.trim());

                    const response = await fetch(url, {
                        headers: { Accept: 'application/json' },
                        signal: controller.signal,
                    });
                    if (!response.ok)
                        throw new Error('Не вдалося завантажити товари.');

                    setOffers((await response.json()) as OrderEditableOffer[]);
                } catch (requestError) {
                    if (!controller.signal.aborted) {
                        setError((requestError as Error).message);
                        setOffers([]);
                    }
                } finally {
                    if (!controller.signal.aborted) setLoading(false);
                }
            },
            query ? 300 : 0,
        );

        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [orderId, query]);

    const visibleOffers = offers.filter(
        (offer) => !excludedOfferIds.includes(offer.id),
    );

    return (
        <section className="space-y-3 rounded-xl border bg-muted/20 p-3">
            <div>
                <h3 className="text-sm font-semibold">Додати товар</h3>
                <p className="text-xs text-muted-foreground">
                    Доступні активні пропозиції цього продавця.
                </p>
            </div>
            <div className="relative">
                <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                    type="search"
                    value={query}
                    className="pl-9"
                    placeholder="Пошук за назвою"
                    aria-label="Пошук товарів для замовлення"
                    onChange={(event) => setQuery(event.target.value)}
                />
            </div>
            {loading && (
                <p className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Завантаження…
                </p>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            {!loading && !error && visibleOffers.length === 0 && (
                <p className="text-sm text-muted-foreground">
                    Доступних товарів не знайдено.
                </p>
            )}
            {visibleOffers.length > 0 && (
                <div className="max-h-64 divide-y overflow-y-auto rounded-lg border bg-background">
                    {visibleOffers.map((offer) => (
                        <div
                            key={offer.id}
                            className="flex items-center gap-3 p-2.5"
                        >
                            <div className="h-14 w-10 flex-shrink-0 overflow-hidden rounded bg-muted">
                                <Image
                                    src={
                                        offer.product?.image ||
                                        '/placeholder.svg'
                                    }
                                    alt={offer.product?.name}
                                    className="object-cover"
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="line-clamp-2 text-sm font-medium">
                                    {offer.product?.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {formatOrderAmount(offer.price)} · доступно{' '}
                                    {offer.amount} шт.
                                </p>
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => onAdd(offer)}
                            >
                                <Plus className="mr-1 h-4 w-4" />
                                Додати
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};
